import type { EntityId, EntityWithAuditFields, OmitAuditFields } from '@pkg/db/schema'
import { getTableById } from '@pkg/db/utils'
import { appError } from '@pkg/shared/errors'
import { logger } from '@pkg/shared/logger'
import { createHLC, type HLC, type HLCString } from '@pkg/sync/common/hlc'
import type { SyncDatabase } from '@pkg/sync/common/schema'
import type { Kysely, Transaction } from 'kysely'
import type { z } from 'zod'
import { applyAuditFields, buildMessage, computeDiff, validateMerged } from './mutateInternals'
import { createDeferredTransaction, type DeferredTransaction, type StagedEntry } from './transaction'
import type { SyncMessage } from './types'

export type DatabaseSchema<DB> = Record<
  Extract<keyof Omit<DB, 'message' | 'appMetadata'>, string>,
  z.ZodObject | z.ZodDiscriminatedUnion
>

export interface MutateConfig<DB> {
  db: Kysely<DB>
  nodeId: string | ((conn: Kysely<DB> | Transaction<DB>) => string | Promise<string>)
  userId: string | (() => string | Promise<string>)
  hlc?: HLC
  onMessages?: (trx: Transaction<DB>, messages: SyncMessage[]) => Promise<void>
  ignoredFields?: string[]
  transformMessage?: <T extends SyncMessage>(msg: T) => T
  schema?: DatabaseSchema<DB>
}

export const DEFAULT_IGNORED_FIELDS = [
  'id',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'deletedAt',
  'deletedBy',
  'searchVector',
]

export interface MutateFnOptions<DB extends SyncDatabase> {
  trx?: Transaction<DB> | Kysely<DB>
  /** Pre-allocated transactionTs shared across this mutate call. If omitted, the first column's HLC is used. */
  transactionTs?: HLCString
  /**
   * Override the intentTs used for specific columns. Columns not in this map fall back to
   * `hlc.now()` per column. The synthetic insert message (written when the row does not yet
   * exist) is keyed under `'id'` — set `intentTsByColumn.id` to override its intentTs.
   */
  intentTsByColumn?: Readonly<Record<string, HLCString>>
  /** @internal Override the userId resolution for this call. Used by the deferred flush so all rows share one userId. */
  userId?: string
  /** Route this call to a deferred transaction's staging buffer instead of writing immediately. */
  transaction?: { mutate: MutateFn<DB> }
}

export type MutateFn<DB extends SyncDatabase> = <T extends OmitAuditFields<EntityWithAuditFields>>(
  data: T,
  opts?: MutateFnOptions<DB>,
) => Promise<T>

export type RemoveFnOptions<DB extends SyncDatabase> = {
  trx?: Transaction<DB> | Kysely<DB>
  transactionTs?: HLCString
  intentTsByColumn?: Readonly<Record<string, HLCString>>
  /** Route this call to a deferred transaction's staging buffer instead of writing immediately. */
  transaction?: { remove: RemoveFn<DB>; restore: RemoveFn<DB> }
}

export type RemoveFn<DB extends SyncDatabase> = (
  id: EntityId<Extract<keyof DB, string>>,
  opts?: RemoveFnOptions<DB>,
) => Promise<void>

export interface CreateMutateResult<DB extends SyncDatabase> {
  mutate: MutateFn<DB>
  remove: RemoveFn<DB>
  restore: RemoveFn<DB>
  beginTransaction: () => DeferredTransaction<DB>
  withTransaction: <T>(fn: (tx: DeferredTransaction<DB>) => Promise<T>) => Promise<T>
}

export function createMutate<DB extends SyncDatabase>(config: MutateConfig<DB>): CreateMutateResult<DB> {
  const {
    db,
    nodeId,
    userId,
    onMessages,
    transformMessage = v => v,
    ignoredFields = DEFAULT_IGNORED_FIELDS,
    schema,
  } = config

  let hlc = config.hlc

  // Lazy-init the closure-scoped hlc. Steady-state: returns the same instance.
  // Race: two parallel first-time callers can both observe `hlc === undefined`,
  // each call `nodeId(conn)` and assign. Last writer wins. Safe because nodeId is
  // stable per client, so any losing instance is structurally identical to the winner.
  const ensureHlc = async (conn: Kysely<DB> | Transaction<DB>): Promise<HLC> => {
    if (hlc) return hlc
    const nid = typeof nodeId === 'function' ? await nodeId(conn) : nodeId
    logger.info({ nodeId: nid }, 'Configuring hlc')
    hlc = createHLC(nid)
    return hlc
  }

  const mutate: MutateFn<DB> = async function mutate<T extends EntityWithAuditFields>(
    data: T,
    opts: MutateFnOptions<DB> = {},
  ) {
    if (opts.transaction) {
      return opts.transaction.mutate(data, { ...opts, transaction: undefined }) as never
    }
    if (!data) {
      throw appError('INTERNAL', 'Data must be provided to be saved')
    }

    if (!data.id) {
      throw appError('INTERNAL', 'Data must have an id to be saved')
    }

    const table = getTableById(data.id)

    const execute = async (trx: Transaction<DB>) => {
      const uid = opts.userId ?? (typeof userId === 'function' ? await userId() : userId)
      logger.debug({ data, table, uid }, `Saving Entity: ${data.id}`)

      const liveHlc = await ensureHlc(trx)

      const existing = (await trx
        .selectFrom(table as never)
        .selectAll()
        // @ts-expect-error ignore
        .where('id' as never, '=', data.id as never)
        .executeTakeFirst()) as T

      const tableSchema = schema ? schema[table as keyof typeof schema] : undefined
      let dataToSave: T = { ...data }
      if (tableSchema) {
        dataToSave = validateMerged<T>(table, tableSchema as unknown as z.ZodType<T>, { ...existing, ...data })
      }

      const changedColumns = computeDiff<T>(existing, dataToSave, ignoredFields)
      if (changedColumns.size === 0) {
        logger.debug({ data, existing }, 'No changes detected, skipping update')
        return existing
      }

      logger.debug({ changedColumns: [...changedColumns], data, existing }, 'Changes detected, proceeding with update')

      const messages: SyncMessage[] = []
      // If transactionTs is not provided, it is seeded from the first column's intentTs below.
      // Callers that supply intentTsByColumn without transactionTs accept that the first
      // column's override also defines transactionTs for this entity write.
      let transactionTs: HLCString | undefined = opts.transactionTs
      let lastIntentTs: HLCString | undefined

      if (!existing) {
        const intentTs = opts.intentTsByColumn?.id ?? liveHlc.now()
        transactionTs = transactionTs ?? intentTs
        lastIntentTs = intentTs
        const msg = buildMessage({
          row: dataToSave.id,
          column: 'id',
          value: dataToSave.id,
          intentTs,
          transactionTs,
          userId: uid,
          transformMessage,
        })
        messages.push(
          (await trx
            .insertInto('message')
            .values(msg as never)
            .returningAll()
            .executeTakeFirst()) as never,
        )
      }

      for (const column of changedColumns) {
        const intentTs = opts.intentTsByColumn?.[column] ?? liveHlc.now()
        transactionTs = transactionTs ?? intentTs
        lastIntentTs = intentTs
        const msg = buildMessage({
          row: data.id,
          column,
          value: data[column as keyof T],
          intentTs,
          transactionTs,
          userId: uid,
          transformMessage,
        })
        messages.push(
          (await trx
            .insertInto('message')
            .values(msg as never)
            .returningAll()
            .executeTakeFirst()) as never,
        )
      }

      if (lastIntentTs) {
        // Bridge: `existing` from selectAll() is wider than the audit-field shape
        applyAuditFields(
          dataToSave,
          lastIntentTs,
          existing as unknown as { createdAt?: string; createdBy?: string } | null,
          uid,
        )
      }

      await onMessages?.(trx, messages)

      return { ...existing, ...dataToSave }
    }

    return await (opts.trx ? execute(opts.trx as Transaction<DB>) : db.transaction().execute(execute))
  }

  const writeDeleteIntent = async (id: string, isRestore: boolean, opts: RemoveFnOptions<DB>): Promise<void> => {
    const execute = async (trx: Transaction<DB>) => {
      const uid = typeof userId === 'function' ? await userId() : userId
      const liveHlc = await ensureHlc(trx)
      const intentTs = opts.intentTsByColumn?.deletedAt ?? liveHlc.now()
      const transactionTs = opts.transactionTs ?? intentTs
      const msg = buildMessage({
        row: id,
        column: 'deletedAt',
        value: isRestore ? null : intentTs,
        intentTs,
        transactionTs,
        userId: uid,
        transformMessage,
      })
      const inserted = await trx
        .insertInto('message')
        .values(msg as never)
        .returningAll()
        .executeTakeFirst()
      await onMessages?.(trx, [inserted as never])
    }
    await (opts.trx ? execute(opts.trx as Transaction<DB>) : db.transaction().execute(execute))
  }

  const remove: RemoveFn<DB> = (id, opts = {}) => {
    if (opts.transaction) return opts.transaction.remove(id, { ...opts, transaction: undefined })
    return writeDeleteIntent(id, false, opts)
  }

  const restore: RemoveFn<DB> = (id, opts = {}) => {
    if (opts.transaction) return opts.transaction.restore(id, { ...opts, transaction: undefined })
    return writeDeleteIntent(id, true, opts)
  }

  const flushStagedEntries = async (entries: ReadonlyArray<StagedEntry>, transactionTs: HLCString): Promise<void> => {
    await db.transaction().execute(async trx => {
      const liveHlc = await ensureHlc(trx)
      const uid = typeof userId === 'function' ? await userId() : userId
      const allMessages: SyncMessage[] = []

      for (const entry of entries) {
        const { table, id, baseExisting, pending, intentTsByColumn } = entry
        const merged = { ...(baseExisting ?? {}), ...pending } as Record<string, unknown>

        // Re-validate at flush — duplicate of stage-time validation, intentional safety.
        const tableSchema = schema ? schema[table as keyof typeof schema] : undefined
        const dataToSave = tableSchema
          ? validateMerged<Record<string, unknown>>(
              table,
              tableSchema as unknown as z.ZodType<Record<string, unknown>>,
              merged,
            )
          : merged

        const changedColumns = computeDiff(baseExisting as object | null, dataToSave, ignoredFields)
        if (changedColumns.size === 0 && baseExisting && !intentTsByColumn.deletedAt) continue

        const messages: SyncMessage[] = []

        if (!baseExisting) {
          const intentTs = intentTsByColumn.id ?? liveHlc.now()
          const msg = buildMessage({
            row: id,
            column: 'id',
            value: id,
            intentTs,
            transactionTs,
            userId: uid,
            transformMessage,
          })
          messages.push(
            (await trx
              .insertInto('message')
              .values(msg as never)
              .returningAll()
              .executeTakeFirst()) as never,
          )
        }

        for (const column of changedColumns) {
          const intentTs = intentTsByColumn[column as string] ?? liveHlc.now()
          const msg = buildMessage({
            row: id,
            column: column as string,
            value: dataToSave[column as string],
            intentTs,
            transactionTs,
            userId: uid,
            transformMessage,
          })
          messages.push(
            (await trx
              .insertInto('message')
              .values(msg as never)
              .returningAll()
              .executeTakeFirst()) as never,
          )
        }

        // Carve-out: emit deletedAt message if staged by tx.remove() or tx.restore().
        // createDeferredTransaction stores the HLC intentTs string for remove, or the
        // literal 'restore' sentinel for restore. The message value is null for restore
        // and intentTs for delete; applyMessage derives the actual deletedAt timestamp
        // from intentTs rather than from value.
        const deleteKey = intentTsByColumn.deletedAt as string | undefined
        if (deleteKey !== undefined) {
          const isRestore = deleteKey === 'restore'
          const intentTs = isRestore ? liveHlc.now() : (deleteKey as HLCString)
          const value = isRestore ? null : intentTs
          const msg = buildMessage({
            row: id,
            column: 'deletedAt',
            value,
            intentTs,
            transactionTs,
            userId: uid,
            transformMessage,
          })
          messages.push(
            (await trx
              .insertInto('message')
              .values(msg as never)
              .returningAll()
              .executeTakeFirst()) as never,
          )
        }

        // Audit fields (createdAt/createdBy/updatedAt/updatedBy) land on the projection row
        // via `applyMessage`, which derives them from each message's intentTs/userId. The
        // deferred path has no return value, so unlike the immediate `mutate` we don't need
        // to populate audit fields on a local copy here.
        allMessages.push(...messages)
      }

      if (allMessages.length > 0) {
        await onMessages?.(trx, allMessages)
      }
    })
  }

  const beginTransaction = (): DeferredTransaction<DB> => {
    return createDeferredTransaction<DB>({
      ensureHlc: () => ensureHlc(db as never),
      readExisting: async (table, id) => {
        return (await db
          .selectFrom(table as never)
          .selectAll()
          // @ts-expect-error structural id lookup across heterogeneous tables
          .where('id' as never, '=', id as never)
          .executeTakeFirst()) as never
      },
      validate: (table, merged) => {
        const tableSchema = schema ? schema[table as keyof typeof schema] : undefined
        if (!tableSchema) return merged
        return validateMerged(table, tableSchema as unknown as z.ZodType<typeof merged>, merged) as never
      },
      flush: flushStagedEntries,
    })
  }

  const withTransaction = async <T>(fn: (tx: DeferredTransaction<DB>) => Promise<T>): Promise<T> => {
    const tx = beginTransaction()
    try {
      const result = await fn(tx)
      await tx.commit()
      return result
    } catch (err) {
      tx.rollback()
      throw err
    }
  }

  return { mutate, remove, restore, beginTransaction, withTransaction }
}
